import type { AuthClientErrorCodes } from "@repo/auth/client";

/**
 * Custom OAuth error codes not in Better Auth's default error codes
 */
type OAuthErrorCodes =
	| "OAUTH_ACCESS_DENIED"
	| "OAUTH_ACCOUNT_ALREADY_LINKED"
	| "OAUTH_CALLBACK_ERROR"
	| "STATE_NOT_FOUND"
	| "INVALID_CALLBACK_REQUEST";

/**
 * Auth error messages map - includes both Better Auth and custom OAuth error codes.
 * All messages are user-friendly and avoid exposing internal details.
 */
export const AUTH_ERROR_MESSAGES: Partial<
	Record<keyof AuthClientErrorCodes | OAuthErrorCodes | string, string>
> = {
	INVALID_TOKEN:
		"This link has expired or is invalid. Please request a new one.",
	PASSWORD_TOO_SHORT: "Password must be at least 12 characters.",
	PASSWORD_TOO_LONG: "Password is too long.",
	INVALID_PASSWORD: "The password you entered is incorrect.",
	INVALID_EMAIL: "Please enter a valid email address.",
	USER_NOT_FOUND: "No account found with this email address.",
	USER_ALREADY_EXISTS: "An account with this email already exists.",
	EMAIL_NOT_VERIFIED: "Please verify your email address first.",
	INVALID_EMAIL_OR_PASSWORD: "Invalid email or password.",
	SOCIAL_ACCOUNT_ALREADY_LINKED:
		"This social account is already linked to another user.",
	CREDENTIAL_ACCOUNT_NOT_FOUND:
		"No password set for this account. Try signing in with a social provider.",
	SESSION_EXPIRED: "Your session has expired. Please sign in again.",
	FAILED_TO_CREATE_USER: "Failed to create account. Please try again.",
	TOO_MANY_REQUESTS: "Too many attempts. Please try again later.",
	FAILED_TO_CREATE_SESSION: "Failed to create session. Please try again.",
	FAILED_TO_UPDATE_USER: "Failed to update user. Please try again.",
	FAILED_TO_GET_SESSION: "Failed to get session. Please sign in again.",
	EMAIL_CAN_NOT_BE_UPDATED: "Email cannot be updated.",
	FAILED_TO_GET_USER_INFO: "Failed to get user info.",
	ID_TOKEN_NOT_SUPPORTED: "ID token not supported.",
	PROVIDER_NOT_FOUND: "Provider not found.",
	USER_EMAIL_NOT_FOUND: "No account found with this email address.",
	USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL:
		"An account with this email already exists.",
	INVALID_INVITATION:
		"There's no account or invitation for this email. Ask your admin to invite you.",
	FAILED_TO_UNLINK_LAST_ACCOUNT:
		"Cannot unlink your last account. You need at least one sign-in method.",
	ACCOUNT_NOT_FOUND: "Account not found.",
	// OAuth-specific errors
	OAUTH_ACCESS_DENIED:
		"Access was denied. Please allow the required permissions.",
	OAUTH_ACCOUNT_ALREADY_LINKED:
		"This social account is already linked to another user.",
	OAUTH_CALLBACK_ERROR: "Authentication failed. Please try again.",
	STATE_NOT_FOUND: "Security validation failed. Please try signing in again.",
	INVALID_CALLBACK_REQUEST:
		"Invalid authentication response. Please try again.",
};

/**
 * Get user-friendly auth error message from an error code or error object.
 */
export function getAuthErrorMessage(
	error: { code?: string; message?: string } | string | null | undefined,
): string {
	if (!error) {
		return "An unknown error occurred.";
	}

	const code = typeof error === "string" ? error : error.code;
	const message = typeof error === "string" ? undefined : error.message;

	if (code && AUTH_ERROR_MESSAGES[code as keyof typeof AUTH_ERROR_MESSAGES]) {
		return AUTH_ERROR_MESSAGES[
			code as keyof typeof AUTH_ERROR_MESSAGES
		] as string;
	}

	return message || "An unexpected error occurred. Please try again.";
}
